// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AdvisoryInstitution } from '@shared/data-access/types/brokerage';
import { isNullOrUndefined } from '@shared/utils';

/** Nuvara Inversis institution ID. */
export const NUVARA_INVERSIS_INSTITUTION_ID = 'nuvara_inversis' as const;

/**
 * Placeholder IBAN for advisory proposals when the client has not completed Nuvara
 * Inversis account opening.
 */
export const STATIC_INVERSIS_CUSTODY_IBAN = 'INVERSIS-CUSTODY' as const;

/** Cash account IBAN placeholder for proposal operations (maps to checking). */
export const CASH_ACCOUNT_IBAN = 'cash_account' as const;

/**
 * Check if the institution is Nuvara Inversis.
 *
 * @param institution - The institution to check.
 * @returns True if the institution is Nuvara Inversis, false otherwise.
 */
export const isNuvaraInversisInstitution = <
  T extends { id?: string; institution_id?: string } | string,
>(
  institution: T | null | undefined,
): boolean => {
  if (isNullOrUndefined(institution)) {
    return false;
  }

  if (typeof institution === 'string') {
    return institution === NUVARA_INVERSIS_INSTITUTION_ID;
  }

  return (
    institution.id === NUVARA_INVERSIS_INSTITUTION_ID ||
    institution.institution_id === NUVARA_INVERSIS_INSTITUTION_ID
  );
};

type ProposalWithInstitutions = {
  institutions?: AdvisoryInstitution[] | null;
};

/**
 * Whether an advisory proposal includes Inversis custody in its perimeter
 * (`INVERSIS-CUSTODY` IBAN and/or Nuvara Inversis institution).
 */
export const proposalIncludesInversisCustody = (
  proposal: ProposalWithInstitutions | null | undefined,
): boolean => {
  if (isNullOrUndefined(proposal?.institutions)) {
    return false;
  }

  return proposal.institutions.some(institution => {
    if (isNuvaraInversisInstitution(institution)) {
      return true;
    }

    return (institution.products?.accounts ?? []).some(
      account => account.iban === STATIC_INVERSIS_CUSTODY_IBAN,
    );
  });
};
